import type { PosCollection, PosSale, PosSession, PosZReport, TerminalShift, TerminalShiftSale } from '@erp/shared';

import type { DrawerCard, ShiftSaleRow } from './ShiftView.js';

/**
 * How a shift was paid, per currency — the one model the shifts list, its
 * totals, the shift dialogs and the printed close-out all read, whichever
 * program rang the shift up (the .exe till or the web register).
 *
 * Money in different currencies is never added: every figure here belongs to
 * one currency's block.
 */

export type MethodKind = 'cash' | 'card' | 'transfer' | 'wallet' | 'other' | 'cashback' | 'credit';

/** The order methods are listed in, everywhere. */
export const METHOD_ORDER: readonly MethodKind[] = ['cash', 'card', 'transfer', 'wallet', 'other', 'cashback', 'credit'];

export interface CurrencyBreakdown {
  currencyCode: string;
  /** Net takings in this currency. */
  takings: string;
  /** Non-zero methods, in METHOD_ORDER; `count` null when the source does not say. */
  methods: Array<{ kind: MethodKind; total: string; count: number | null }>;
  /** False when the till reported this currency's total but not its split. */
  methodsKnown: boolean;
  returns: { count: number; total: string } | null;
  /** Customers paying off debt, by the method the money came in. */
  payments: Array<{ method: string; total: string; count: number }>;
}

const nonZero = (v: string | number | null | undefined): boolean => Math.abs(Number(v ?? 0)) > 0.004;
const money4 = (n: number): string => String(Math.round(n * 10000) / 10000);

function ordered(codes: Iterable<string>, first: string): string[] {
  return [...new Set(codes)].sort((a, b) => (a === first ? -1 : b === first ? 1 : a.localeCompare(b)));
}

function byOrder<T extends { kind: MethodKind }>(list: T[]): T[] {
  return [...list].sort((a, b) => METHOD_ORDER.indexOf(a.kind) - METHOD_ORDER.indexOf(b.kind));
}

/** A tender kind as this model names it (the register's and the till's words differ). */
export function methodKind(raw: string | null | undefined): MethodKind {
  const k = String(raw ?? 'cash');
  if (k === 'bank_transfer') return 'transfer';
  return (METHOD_ORDER as readonly string[]).includes(k) ? (k as MethodKind) : 'other';
}

function groupPayments(list: Array<{ method: string; total: string | number; count: number }>): CurrencyBreakdown['payments'] {
  const m = new Map<string, { total: number; count: number }>();
  for (const p of list) {
    const e = m.get(p.method) ?? { total: 0, count: 0 };
    e.total += Number(p.total);
    e.count += p.count;
    m.set(p.method, e);
  }
  return [...m.entries()]
    .filter(([, v]) => nonZero(v.total))
    .map(([method, v]) => ({ method, total: money4(v.total), count: v.count }))
    .sort((a, b) => METHOD_ORDER.indexOf(methodKind(a.method)) - METHOD_ORDER.indexOf(methodKind(b.method)));
}

/**
 * A till (.exe) close-out. The shift currency's split is the till's own
 * (it counts anything still queued); the other currencies' split, the counts,
 * returns and payments come from the sales it stamped with the shift.
 */
export function tillBreakdown(s: TerminalShift): CurrencyBreakdown[] {
  const b = s.breakdown ?? null;
  const codes = ordered([
    s.currencyCode,
    ...s.foreignCash.map((f) => f.currencyCode),
    ...(b?.sales.map((x) => x.currencyCode) ?? []),
    ...(b?.returns.map((x) => x.currencyCode) ?? []),
    ...(b?.payments.map((x) => x.currencyCode) ?? []),
  ], s.currencyCode);
  return codes.map((ccy) => {
    const derived = (b?.sales ?? []).filter((x) => x.currencyCode === ccy);
    const countOf = (m: string): number | null => (b ? derived.filter((x) => x.method === m).reduce((n, x) => n + x.count, 0) : null);
    const own = ccy === s.currencyCode;
    const methods = own
      ? ([['cash', s.cashTaken], ['card', s.cardTaken], ['transfer', s.transferTaken], ['credit', s.creditTaken]] as const)
        .filter(([, v]) => nonZero(v))
        // No stamped sale behind an amount (still queued, or an older till): no count, not "0".
        .map(([kind, v]) => ({ kind: kind as MethodKind, total: String(Number(v)), count: countOf(kind) || null }))
      : byOrder(derived.filter((x) => nonZero(x.total)).map((x) => ({ kind: methodKind(x.method), total: String(Number(x.total)), count: x.count })));
    const foreign = s.foreignCash.find((f) => f.currencyCode === ccy);
    const takings = own ? s.totalTaken : foreign?.totalTaken ?? money4(derived.reduce((n, x) => n + Number(x.total), 0));
    const ret = b?.returns.find((x) => x.currencyCode === ccy);
    return {
      currencyCode: ccy,
      takings: String(Number(takings)),
      methods,
      methodsKnown: own || !!b,
      returns: ret && ret.count > 0 ? { count: ret.count, total: ret.total } : null,
      payments: groupPayments((b?.payments ?? []).filter((x) => x.currencyCode === ccy)),
    };
  }).filter((c, i) => i === 0 || nonZero(c.takings) || c.methods.length > 0 || c.returns || c.payments.length > 0);
}

/**
 * A web-register shift: from its Z-report when there is one (fresh, and what
 * the cashier's close dialog shows), else from the list's own split.
 * Whatever the tenders do not cover was sold on account.
 */
export function sessionBreakdown(s: PosSession, z?: PosZReport | null, collections?: PosCollection[] | null): CurrencyBreakdown[] {
  const pays = collections
    ? collections.map((c) => ({ currencyCode: c.notesCurrency, method: c.method, total: c.notesAmount, count: 1 }))
    : (s.collections ?? []);
  const onAccount = (takings: string, tenders: Array<{ total: string }>): number =>
    Number(takings) - tenders.reduce((n, x) => n + Number(x.total), 0);
  const withCredit = (methods: CurrencyBreakdown['methods'], credit: number): CurrencyBreakdown['methods'] =>
    (credit > 0.004 ? [...methods, { kind: 'credit' as const, total: money4(credit), count: null }] : methods);

  if (z?.byCurrency?.length) {
    const codes = ordered([...z.byCurrency.map((c) => c.currencyCode), ...pays.map((p) => p.currencyCode)], s.currencyCode);
    return codes.map((ccy) => {
      const c = z.byCurrency!.find((x) => x.currencyCode === ccy);
      // A cash tender is what was handed over; the change went back out.
      const tenders = (c?.byTender ?? []).map((x) => (x.kind === 'cash'
        ? { ...x, total: String(Number(x.total) - Number(c?.tenderChange ?? 0)) }
        : x));
      const methods = byOrder(tenders.filter((x) => nonZero(x.total)).map((x) => ({ kind: methodKind(x.kind), total: String(Number(x.total)), count: x.count })));
      return {
        currencyCode: ccy,
        takings: String(Number(c?.netSales ?? 0)),
        methods: withCredit(methods, onAccount(c?.netSales ?? '0', tenders)),
        methodsKnown: true,
        returns: c && c.refundCount > 0 ? { count: c.refundCount, total: c.returnsTotal ?? '0' } : null,
        payments: groupPayments(pays.filter((p) => p.currencyCode === ccy)),
      };
    });
  }

  const codes = ordered([
    s.currencyCode,
    ...(s.takings ?? []).map((x) => x.currencyCode),
    ...(s.tenders ?? []).map((x) => x.currencyCode),
    ...(s.returns ?? []).map((x) => x.currencyCode),
    ...pays.map((p) => p.currencyCode),
  ], s.currencyCode);
  return codes.map((ccy) => {
    const takings = (s.takings ?? []).find((x) => x.currencyCode === ccy)?.total ?? '0';
    const tenders = (s.tenders ?? []).filter((x) => x.currencyCode === ccy);
    const methods = byOrder(tenders.filter((x) => nonZero(x.total)).map((x) => ({ kind: methodKind(x.kind), total: String(Number(x.total)), count: x.count })));
    const ret = (s.returns ?? []).find((x) => x.currencyCode === ccy);
    return {
      currencyCode: ccy,
      takings: String(Number(takings)),
      methods: withCredit(methods, onAccount(takings, tenders)),
      methodsKnown: true,
      returns: ret && ret.count > 0 ? { count: ret.count, total: ret.total } : null,
      payments: groupPayments(pays.filter((p) => p.currencyCode === ccy)),
    };
  }).filter((c, i) => i === 0 || nonZero(c.takings) || c.methods.length > 0 || c.returns || c.payments.length > 0);
}

/** Many shifts' blocks added up per currency — the totals above the list. */
export function sumBreakdowns(all: CurrencyBreakdown[][], first: string | null): Array<CurrencyBreakdown & { unsplit: string }> {
  const acc = new Map<string, {
    takings: number; methods: Map<MethodKind, { total: number; count: number | null }>; unsplit: number;
    returns: { count: number; total: number }; payments: Array<{ method: string; total: string; count: number }>;
  }>();
  for (const list of all) {
    for (const c of list) {
      const e = acc.get(c.currencyCode) ?? { takings: 0, methods: new Map<MethodKind, { total: number; count: number | null }>(), unsplit: 0, returns: { count: 0, total: 0 }, payments: [] as CurrencyBreakdown['payments'] };
      e.takings += Number(c.takings);
      if (!c.methodsKnown) e.unsplit += Number(c.takings);
      for (const m of c.methods) {
        const x = e.methods.get(m.kind) ?? { total: 0, count: 0 };
        x.total += Number(m.total);
        x.count = x.count === null || m.count === null ? null : x.count + m.count;
        e.methods.set(m.kind, x);
      }
      if (c.returns) { e.returns.count += c.returns.count; e.returns.total += Number(c.returns.total); }
      e.payments.push(...c.payments);
      acc.set(c.currencyCode, e);
    }
  }
  return ordered(acc.keys(), first ?? '').map((ccy) => {
    const e = acc.get(ccy)!;
    return {
      currencyCode: ccy,
      takings: money4(e.takings),
      methods: byOrder([...e.methods.entries()].filter(([, v]) => nonZero(v.total)).map(([kind, v]) => ({ kind, total: money4(v.total), count: v.count }))),
      methodsKnown: e.unsplit === 0,
      unsplit: money4(e.unsplit),
      returns: e.returns.count > 0 ? { count: e.returns.count, total: money4(e.returns.total) } : null,
      payments: groupPayments(e.payments),
    };
  });
}

/* ------------------------------------------------------------------ drawers */

/**
 * The till's drawer cards. The shift currency's "received" is what the till's
 * slip prints as cash in: expected − float + refunds; whatever of it is beyond
 * the cash SALES in that currency came in for sales in other currencies.
 */
export function tillDrawerCards(shift: TerminalShift): DrawerCard[] {
  const ownReceived = Number(shift.expectedCash) - Number(shift.openingFloat) + Number(shift.refundsCash ?? 0);
  return [
    {
      currencyCode: shift.currencyCode,
      float: shift.openingFloat,
      received: String(ownReceived),
      fromOtherSales: String(ownReceived - Number(shift.cashTaken)),
      paidInOtherNotes: null,
      changeGiven: null,
      paidOut: shift.refundsCash ?? '0',
      paidOutLabel: 'changeAndRefunds',
      expected: shift.expectedCash,
      counted: shift.countedCash,
      difference: Number(shift.difference),
      takings: shift.totalTaken,
      onAccount: shift.creditTaken,
      nonCash: String(Number(shift.cardTaken) + Number(shift.transferTaken) + Number(shift.creditTaken)),
    },
    ...(shift.foreignCash ?? []).map((f): DrawerCard => ({
      currencyCode: f.currencyCode,
      float: null,
      received: f.cashIn,
      fromOtherSales: null,
      paidInOtherNotes: String(Number(f.totalTaken) - Number(f.cashIn)),
      changeGiven: null,
      paidOut: f.cashOut,
      paidOutLabel: 'changeAndRefunds',
      expected: f.expectedCash,
      counted: f.countedCash,
      difference: Number(f.difference),
      takings: f.totalTaken,
    })),
  ];
}

/**
 * The register's drawer cards: one per currency the shift touched, from the
 * Z-report; without it (no permission), a CLOSED shift still has its
 * persisted per-currency counts.
 */
export function sessionDrawerCards(session: PosSession, report: PosZReport | null): DrawerCard[] {
  const ccy = session.currencyCode;
  const open = session.status === 'open';
  if (report?.byCurrency?.length) {
    return report.byCurrency.map((c) => ({
      currencyCode: c.currencyCode,
      float: c.currencyCode === ccy ? c.openingCash : null,
      received: c.cashReceived ?? String(Number(c.expectedCash) - Number(c.openingCash)),
      fromOtherSales: c.fromOtherSales ?? null,
      paidInOtherNotes: c.paidInOtherNotes ?? null,
      changeGiven: c.changeGiven ?? null,
      paidOut: c.cashRefunds ?? null,
      paidOutLabel: 'refunds',
      expected: c.expectedCash,
      counted: open ? null : c.countedCash,
      difference: open || c.variance === null ? null : Number(c.variance),
      takings: c.netSales,
      // A sale on account takes no tender: what the tenders do not cover was sold unpaid.
      onAccount: String(Number(c.netSales) - c.byTender.reduce((acc, x) => acc + Number(x.total), 0)),
      collected: c.collectedCash ?? null,
      collectedOther: c.collectedOther ?? null,
      nonCash: String(
        c.byTender.filter((x) => x.kind !== 'cash').reduce((acc, x) => acc + Number(x.total), 0)
        + Math.max(0, Number(c.netSales) - c.byTender.reduce((acc, x) => acc + Number(x.total), 0)),
      ),
    }));
  }
  return (session.drawerCounts ?? []).map((d) => ({
    currencyCode: d.currencyCode,
    float: d.currencyCode === ccy ? d.openingCash : null,
    received: String(Number(d.expected) - Number(d.openingCash)),
    fromOtherSales: null,
    paidInOtherNotes: null,
    changeGiven: null,
    paidOut: null,
    paidOutLabel: 'refunds',
    expected: d.expected,
    counted: d.counted,
    difference: Number(d.variance),
    takings: session.takings?.find((x) => x.currencyCode === d.currencyCode)?.total ?? '0',
  }));
}

/* ------------------------------------------------------------------ sales lines */

type T = (key: string, opts?: Record<string, unknown>) => string;
type Money = (v: string | number, ccy: string) => string;

/** The till shift's sales as list rows (with the raw method, for the printed groups). */
export function tillSaleRows(items: TerminalShiftSale[], shiftCurrency: string, t: T, money: Money): ShiftSaleRow[] {
  return items.map((s) => ({
    key: s.docNumber + s.syncedAt,
    number: s.docNumber,
    party: s.partyName,
    at: s.syncedAt,
    currencyCode: s.currencyCode ?? shiftCurrency,
    total: s.total,
    kind: s.kind,
    // How the customer paid — the till's own marker; older rows read cash.
    methods: s.paymentMethod ? [t(`shifts.${s.paymentMethod}`, { defaultValue: s.paymentMethod })] : [],
    methodKinds: s.paymentMethod ? [methodKind(s.paymentMethod)] : [],
    // A dollar sale paid in dinars: the dinars the customer handed over, net of change from them.
    paidWith: s.cashIn && s.cashIn.currencyCode !== (s.currencyCode ?? shiftCurrency)
      ? money(Number(s.cashIn.amount) - (s.cashBack && s.cashBack.currencyCode === s.cashIn.currencyCode ? Number(s.cashBack.amount) : 0), s.cashIn.currencyCode)
      : null,
    // The INVOICE is the booked, printable paper; the order stays as the fallback.
    invoiceHref: s.invoiceId ? (s.kind === 'return' ? `/sales/returns/${s.invoiceId}` : `/sales/invoices/${s.invoiceId}`) : null,
    orderHref: s.salesOrderId ? `/sales/orders/${s.salesOrderId}` : null,
  }));
}

/**
 * The register shift's sales as list rows, and every customer who paid off
 * their debt in it. Parked carts are unfinished business, not takings; a
 * voided sale stays, labelled — money rung up and taken back is exactly what a
 * review wants.
 */
export function sessionSaleRows(sales: PosSale[], collections: PosCollection[], t: T, money: Money): ShiftSaleRow[] {
  return sales
    .filter((s) => s.status !== 'parked')
    .map((s): ShiftSaleRow => {
      const total = Number(s.total);
      const refunded = Number(s.refundedTotal ?? 0);
      const unpaid = s.status !== 'voided' && total > 0 && !(s.tenderKinds ?? []).length;
      return {
        key: s.id,
        number: s.saleNumber,
        party: s.partyName ?? null,
        at: s.finalizedAt ?? s.saleDate,
        currencyCode: s.currencyCode,
        total: String(Math.abs(total)),
        kind: s.status === 'voided' ? 'voided' : total < 0 ? 'return' : 'sale',
        methods: [
          ...(s.tenderKinds ?? []).map((k) => t(`shifts.${k}`, { defaultValue: k })),
          // Finalized with no tender at all = sold on account (unpaid).
          ...(unpaid ? [t('shifts.credit', { defaultValue: 'On account' })] : []),
          ...(refunded > 0 ? [`${t('shifts.refundedTag', { defaultValue: 'refunded' })} ${money(refunded, s.currencyCode)}`] : []),
        ],
        methodKinds: [...(s.tenderKinds ?? []).map(methodKind), ...(unpaid ? ['credit' as const] : [])],
        // A dollar sale paid in dinars: the dinars that stayed, net of change from them.
        paidWith: s.cashIn && s.cashIn.currencyCode !== s.currencyCode
          ? money(Number(s.cashIn.amount) - (s.cashBack && s.cashBack.currencyCode === s.cashIn.currencyCode ? Number(s.cashBack.amount) : 0), s.cashIn.currencyCode)
          : null,
        invoiceHref: s.invoiceId ? `/sales/invoices/${s.invoiceId}` : null,
        orderHref: null,
      };
    })
    // A debt payment sits with the debt it paid, like a sale with its currency;
    // dinars for a dollar debt show as "paid …".
    .concat(collections.map((c): ShiftSaleRow => ({
      key: `col-${c.id}`,
      number: c.paymentNumber,
      party: c.partyName,
      at: c.createdAt,
      currencyCode: c.currencyCode,
      total: c.amount,
      kind: 'payment',
      methods: [t(`tender.${c.method}`, { defaultValue: c.method })],
      methodKinds: [methodKind(c.method)],
      paidWith: c.notesCurrency !== c.currencyCode ? money(c.notesAmount, c.notesCurrency) : null,
      invoiceHref: `/payments/${c.paymentId}/print`,
      orderHref: null,
    })));
}
